import React, { useState } from 'react';
import { cpGetAuthHeader } from './ClientPortalApp';

const API = '/api/client/permit-search/';

const ClientPermitSearch = () => {
    const [idf, setIdf] = useState('');
    const [bl, setBl] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!idf.trim() && !bl.trim()) {
            setError('Please enter an IDF number or BL number to search.');
            return;
        }
        setError('');
        setLoading(true);
        setResults(null);
        try {
            const params = new URLSearchParams();
            if (idf.trim()) params.append('idf', idf.trim());
            if (bl.trim()) params.append('bl', bl.trim());
            const res = await fetch(`${API}?${params.toString()}`, { headers: cpGetAuthHeader() });
            if (!res.ok) {
                const d = await res.json();
                setError(d.error || 'Search failed.');
                return;
            }
            const data = await res.json();
            setResults(data);
        } catch {
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="cp-page-header">
                <h2>Required Permit Search</h2>
            </div>

            <div className="cp-card">
                <form onSubmit={handleSearch}>
                    <div className="cp-filters">
                        <div className="cp-filter-group">
                            <label>IDF Number</label>
                            <input
                                type="text"
                                placeholder="Enter IDF No"
                                value={idf}
                                onChange={e => setIdf(e.target.value)}
                            />
                        </div>
                        <div className="cp-filter-group" style={{ alignItems: 'center', justifyContent: 'flex-end', paddingTop: 20, color: '#94a3b8', fontWeight: 600 }}>
                            OR
                        </div>
                        <div className="cp-filter-group">
                            <label>BL Number</label>
                            <input
                                type="text"
                                placeholder="Enter BL No"
                                value={bl}
                                onChange={e => setBl(e.target.value)}
                            />
                        </div>
                        <button type="submit" className="cp-btn cp-btn-primary" style={{ alignSelf: 'flex-end' }} disabled={loading}>
                            <i className="fa-solid fa-search"></i> {loading ? 'Searching…' : 'Search'}
                        </button>
                    </div>
                    {error && (
                        <div style={{ color: '#b91c1c', background: '#fee2e2', borderRadius: 6, padding: '8px 14px', marginTop: 8, fontSize: '0.875rem' }}>
                            {error}
                        </div>
                    )}
                </form>
            </div>

            {loading && (
                <div className="cp-loader"><i className="fa-solid fa-circle-notch fa-spin"></i> Searching…</div>
            )}

            {results !== null && !loading && (
                results.length === 0 ? (
                    <div className="cp-empty">No matching jobs found for the given search criteria.</div>
                ) : (
                    results.map(job => (
                        <div className="cp-card" key={job.job_id} style={{ marginBottom: 20 }}>
                            {/* Job header */}
                            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
                                <div className="cp-detail-field">
                                    <label>KK File No</label>
                                    <div className="value">{job.kk_file_no || '-'}</div>
                                </div>
                                <div className="cp-detail-field">
                                    <label>IDF No</label>
                                    <div className="value">{job.idf_no || '-'}</div>
                                </div>
                                <div className="cp-detail-field">
                                    <label>BL No</label>
                                    <div className="value">{job.mbl_no || '-'}</div>
                                </div>
                                <div className="cp-detail-field">
                                    <label>TANSAD No</label>
                                    <div className="value">{job.tansad_no || '-'}</div>
                                </div>
                                <div className="cp-detail-field">
                                    <label>UCR No</label>
                                    <div className="value">{job.ucr_no || '-'}</div>
                                </div>
                            </div>

                            {(() => {
                                const byType = new Map();
                                job.hs_codes.forEach(hs => {
                                    (job.permits_by_hs[hs] || []).forEach(p => {
                                        if (!byType.has(p.permit)) byType.set(p.permit, new Map());
                                        const byHS = byType.get(p.permit);
                                        const items = p.items && p.items.length ? p.items : (p.description ? [p.description] : ['-']);
                                        byHS.set(p.hs_code, items);
                                    });
                                });

                                const colors = ['#e5def1', '#def1e5', '#f1e5de', '#f1def1', '#e0f0ff', '#fff0e0'];
                                const sortedTypes = [...byType.keys()].sort((a, b) => a.localeCompare(b));

                                return sortedTypes.length === 0 ? (
                                    <div className="cp-empty" style={{ padding: '10px 0' }}>No permit requirements found for this job.</div>
                                ) : (
                                    sortedTypes.map((permit, idx) => {
                                        const byHS = byType.get(permit);
                                        const rows = [...byHS.entries()]
                                            .map(([hs_code, items]) => ({ hs_code, items }))
                                            .sort((a, b) => a.hs_code.localeCompare(b.hs_code));
                                        const color = colors[idx % colors.length];
                                        return (
                                            <div key={permit} style={{ marginBottom: 20, backgroundColor: color, borderRadius: 6, padding: 12 }}>
                                                <div className="cp-section-title" style={{ marginTop: 0 }}>
                                                    Permit Type: <span style={{ color: '#1e293b', fontWeight: 700 }}>{permit}</span>
                                                </div>
                                                <div className="cp-table-wrap">
                                                    <table className="cp-table">
                                                        <thead>
                                                            <tr>
                                                                <th>HS Code</th>
                                                                <th>Items</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {rows.map((row, i) => (
                                                                <tr key={`${row.hs_code}-${i}`}>
                                                                    <td>{row.hs_code}</td>
                                                                    <td>
                                                                        <ul className="cp-items-list">
                                                                            {row.items.map((item, j) => (
                                                                                <li key={j}>{item}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    })
                                );
                            })()}
                        </div>
                    ))
                )
            )}
        </>
    );
};

export default ClientPermitSearch;
